import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articles as allArticles, articleCategories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';
import type { Article } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'tiktok-game-ad-optimization-2026': 'https://readdy.ai/api/search-image?query=Modern%20mobile%20phone%20displaying%20colorful%20casual%20mobile%20game%20with%20TikTok%20style%20creative%20video%20ads%20floating%20around%2C%20tech%20marketing%20office%20background%20with%20warm%20ambient%20lighting%2C%20editorial%20photography%20style%2C%20clean%20composition%2C%20soft%20shadows%2C%20professional%20product%20shot&width=800&height=450&seq=article-tiktok-2026&orientation=landscape',
  'game-ua-data-attribution-2026': 'https://readdy.ai/api/search-image?query=Abstract%20data%20analytics%20dashboard%20with%20colorful%20charts%20and%20graphs%20showing%20marketing%20attribution%20models%2C%20modern%20minimalist%20office%20workspace%20with%20multiple%20monitors%2C%20warm%20amber%20accent%20lighting%2C%20professional%20tech%20environment%2C%20clean%20and%20bright%20composition&width=800&height=450&seq=article-data-attribution&orientation=landscape',
  'playable-ads-design-trends-2026': 'https://readdy.ai/api/search-image?query=Creative%20mobile%20game%20playable%20ad%20design%20concept%20with%20interactive%20game%20elements%20floating%20on%20smartphone%20screen%2C%20colorful%20casual%20game%20art%20style%2C%20modern%20creative%20studio%20background%20with%20warm%20lighting%2C%20professional%20product%20photography&width=800&height=450&seq=article-playable-ads&orientation=landscape',
  'southeast-asia-game-market-2026': 'https://readdy.ai/api/search-image?query=Southeast%20Asia%20cityscape%20with%20digital%20game%20elements%20overlay%2C%20colorful%20neon%20lights%20reflecting%20modern%20gaming%20culture%2C%20warm%20sunset%20skyline%20of%20Bangkok%20and%20Jakarta%2C%20tech%20style%20illustration%2C%20vibrant%20and%20dynamic%20composition&width=800&height=450&seq=article-sea-market&orientation=landscape',
  'slg-game-global-ua-case-study': 'https://readdy.ai/api/search-image?query=Medieval%20strategy%20game%20art%20with%20castle%20siege%20scene%2C%20dramatic%20lighting%20with%20warm%20golden%20accents%2C%20professional%20game%20concept%20art%20style%2C%20dark%20moody%20atmosphere%20with%20fire%20and%20battle%20elements%2C%20epic%20fantasy%20composition&width=800&height=450&seq=article-slg-case&orientation=landscape',
  'google-ads-game-campaign-2026': 'https://readdy.ai/api/search-image?query=Google%20Ads%20dashboard%20interface%20on%20modern%20desktop%20monitor%2C%20colorful%20data%20charts%20and%20campaign%20metrics%2C%20warm%20office%20environment%20with%20natural%20light%2C%20professional%20digital%20marketing%20workspace%2C%20clean%20tech%20aesthetic&width=800&height=450&seq=article-google-ads&orientation=landscape',
  'game-aso-strategy-2026': 'https://readdy.ai/api/search-image?query=App%20Store%20and%20Google%20Play%20store%20listings%20displayed%20on%20smartphone%20screens%2C%20colorful%20game%20icons%20and%20screenshots%2C%20modern%20minimalist%20desk%20setup%20with%20warm%20lighting%2C%20professional%20app%20marketing%20concept%2C%20clean%20composition&width=800&height=450&seq=article-aso&orientation=landscape',
  'game-creative-localization-guide': 'https://readdy.ai/api/search-image?query=Creative%20game%20advertising%20materials%20in%20multiple%20languages%20and%20cultural%20styles%20displayed%20on%20a%20modern%20designer%20desk%2C%20colorful%20mobile%20game%20art%20variations%2C%20warm%20creative%20studio%20lighting%2C%20professional%20multicultural%20marketing%20concept&width=800&height=450&seq=article-localization&orientation=landscape',
  'casual-game-mena-market': 'https://readdy.ai/api/search-image?query=Modern%20Dubai%20skyline%20with%20futuristic%20digital%20game%20elements%20overlay%2C%20warm%20golden%20desert%20sunset%20colors%2C%20luxurious%20Middle%20East%20cityscape%2C%20tech%20and%20gaming%20culture%20fusion%2C%20vibrant%20professional%20illustration&width=800&height=450&seq=article-mena-market&orientation=landscape',
  'game-rewarded-video-ads-guide': 'https://readdy.ai/api/search-image?query=Mobile%20game%20screen%20showing%20rewarded%20video%20ad%20interface%20with%20colorful%20rewards%2C%20casual%20game%20art%20style%2C%20modern%20smartphone%20display%2C%20warm%20and%20inviting%20game%20environment%2C%20clean%20UI%20design%20concept&width=800&height=450&seq=article-rewarded-video&orientation=landscape',
};

export default function ArticlesPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredArticles, setFilteredArticles] = useState<Article[]>(allArticles);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let result = allArticles;
    if (activeCategory !== 'all') {
      result = result.filter((a) => a.categorySlug === activeCategory);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(term) ||
          a.excerpt.toLowerCase().includes(term) ||
          a.tags.some((t) => t.toLowerCase().includes(term))
      );
    }
    setFilteredArticles(result);
  }, [activeCategory, searchTerm]);

  const activeCategoryName = articleCategories.find((c) => c.slug === activeCategory)?.name || '全部文章';

  return (
    <>
      <PageSEO
        title="行业洞察 - 木瓜玩游戏出海 | 广告投放·数据分析·创意素材"
        description="木瓜玩行业洞察频道，汇集海外游戏发行、广告投放优化、数据分析、创意素材制作、市场趋势等专业内容，助力游戏出海企业掌握最新营销方法论。"
        keywords="游戏出海攻略,广告投放教程,游戏数据分析,创意素材设计,海外游戏市场,出海营销案例"
        canonicalUrl="https://steamvip520.cn/articles"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "木瓜玩行业洞察",
          "description": "海外游戏发行与用户增长专业内容平台",
          "url": "https://steamvip520.cn/articles",
          "isPartOf": {
            "@type": "WebSite",
            "name": "木瓜玩",
            "url": "https://steamvip520.cn/"
          }
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="px-6 md:px-10 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '行业洞察' },
              ]}
            />
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mt-4 mb-3">
              行业洞察
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              汇集游戏出海营销最新趋势、实战案例与方法论，助您在全球市场精准获客
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex flex-wrap items-center gap-2">
                {articleCategories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="relative sm:ml-auto w-full sm:w-64">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="搜索文章..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>
            </div>
          </div>
        </div>

        <section className="px-6 md:px-10 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">未找到匹配的文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/articles/${article.slug}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative overflow-hidden h-44 md:h-48">
                      <img
                        src={articleImages[article.slug] || ''}
                        alt={article.title}
                        title={`${article.title} - 木瓜玩`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-xs font-medium text-foreground-700 rounded">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                        <span>{article.publishDate}</span>
                        <span>·</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <p className="text-center text-xs text-foreground-400 mt-10">
              共 {filteredArticles.length} 篇文章 · 当前分类：{activeCategoryName}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}